/**
 * Application error reporting layer.
 * All app code should call these helpers instead of Sentry APIs directly
 * (except Sentry.setUser in auth actions).
 */

import * as Sentry from '@sentry/nextjs';

import { isSentryDisabledByFlag } from '@/lib/sentry/thirdPartyFlags';

type CaptureContext = {
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  level?: 'fatal' | 'error' | 'warning' | 'info';
  /** Becomes exception.type so Sentry titles `{source}: {message}` instead of `<anonymous>`. */
  source?: string;
  /** Stable grouping key. Omit SKUs so similar events stay one issue. */
  fingerprint?: string[];
};

const CONTENTFUL_BROKEN_URL = /cdn\.contentful\.com|images\.ctfassets\.net/i;

function shouldSilence(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return CONTENTFUL_BROKEN_URL.test(message) && /404|not found|failed to fetch/i.test(message);
}

function applySourceName(error: Error, source?: string): Error {
  if (!source || (error.name && error.name !== 'Error')) return error;
  const named = new Error(error.message);
  named.name = source;
  named.stack = error.stack;
  if (error.cause !== undefined) named.cause = error.cause;
  return named;
}

function toNamedError(error: unknown, source?: string): Error {
  if (error instanceof Error) return applySourceName(error, source);
  const wrapped = new Error(typeof error === 'string' ? error : String(error));
  wrapped.name = source ?? 'Error';
  return wrapped;
}

function reportException(
  error: unknown,
  context: CaptureContext | undefined,
  defaultLevel: NonNullable<CaptureContext['level']>
): string {
  const named = toNamedError(error, context?.source);
  const tags = {
    ...context?.tags,
    ...(context?.source ? { source: context.source } : {}),
  };

  return Sentry.captureException(named, {
    level: context?.level ?? defaultLevel,
    tags,
    extra: context?.extra,
    ...(context?.fingerprint ? { fingerprint: context.fingerprint } : {}),
  });
}

function logEventId(eventId: string): void {
  if (process.env.NODE_ENV === 'development' || process.env.NEXT_PUBLIC_SENTRY_DEBUG === 'true') {
    console.info('[Sentry] event', eventId);
  }
}

export function captureError(error: unknown, context?: CaptureContext): string | undefined {
  if (isSentryDisabledByFlag()) return undefined;
  if (shouldSilence(error)) return undefined;

  const eventId = reportException(error, context, 'error');
  logEventId(eventId);
  return eventId;
}

export function captureMessage(message: string, context?: CaptureContext): string | undefined {
  if (isSentryDisabledByFlag()) return undefined;

  const source = context?.source ?? 'App';
  const eventId = reportException(
    new Error(message),
    {
      ...context,
      source,
      fingerprint: context?.fingerprint ?? [source, message],
    },
    'warning'
  );
  logEventId(eventId);
  return eventId;
}

/** 404s are expected traffic, not incidents. */
export function capture404Event(): void {
  // no-op
}
